import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, Eye, FileCheck, Sparkles, Check, Lock, Eraser } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { CREDIT_PACKS } from '../hooks/useCreditCalculator';
import Footer from '../components/layout/Footer';

export default function HomePage() {
  const [showContact, setShowContact] = useState(false);
  // Features data
  const features = [
    {
      icon: Shield,
      title: "POPIA Compliance",
      description:
        "Automatically identify and redact personal information as defined by the Protection of Personal Information Act.",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description:
        "Process thousands of documents in minutes. Redact ID numbers, addresses, and contact details instantly.",
    },
    {
      icon: Eye,
      title: "Smart Detection",
      description: "Automatically detect SA ID numbers, contact details, addresses, and other personal information.",
    },
    {
      icon: FileCheck,
      title: "Audit Trail",
      description:
        "Complete transparency with detailed logs of every redaction for POPIA compliance and accountability.",
    },
    {
      icon: Lock,
      title: "Data Privacy",
      description: (
        <>
          Your data is encrypted and never shared with third parties. Learn more in our{" "}
          <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> or read{" "}
          <Link to="/blog/where-does-your-data-go" className="underline hover:text-white">where your data goes</Link>.
        </>
      ),
    },
    {
      icon: Eraser,
      title: "Irreversible Redaction",
      description:
        "Not just black boxes — sensitive data is permanently removed from the PDF at the byte level, making it completely unrecoverable.",
    },
  ];

  return (
    <div className="min-h-screen gradient-bg">
      {/* Header Section */}
      <header className="border-b border-white/20 bg-transparent">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-4xl font-black tracking-tight text-white font-rajdhani">
              SureDox
            </Link>

            <nav className="flex items-center gap-8">
              <a href="#pricing" className="text-sm font-medium text-white hover:text-white/80 transition-colors">
                Pricing
              </a>
              <Link to="/blog" className="text-sm font-medium text-white hover:text-white/80 transition-colors">
                Blog
              </Link>
              <button onClick={() => setShowContact(true)} className="text-sm font-medium text-white hover:text-white/80 transition-colors">
                Contact
              </button>
              <Link to="/login" className="text-sm font-medium text-white hover:text-white/80 transition-colors">
                Login
              </Link>
              <Link to="/register">
                <Button size="sm" className="font-semibold bg-white text-black hover:bg-gray-200">
                  Register
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-12">
            <div className="space-y-8">
              <h1 className="text-4xl font-black tracking-tight text-balance sm:text-7xl lg:text-8xl xl:text-7xl leading-[0.95] text-white">
                PDF Redaction
                <br />
                AI Assistant
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                Automatically redact personal information. Fast, scalable, effortless.
              </p>
            </div>

            <div className="flex justify-end">
              <div className="relative">
                <div className="aspect-[3/4] w-48 rounded-lg bg-white shadow-xl p-4 flex flex-col relative overflow-hidden border-2 border-gray-200">
                  <div className="mb-3 pb-2 border-b border-gray-300">
                    <div className="h-1.5 bg-gray-800 rounded w-2/3 mb-1"></div>
                    <div className="h-1 bg-gray-400 rounded w-1/2"></div>
                  </div>

                  <div className="space-y-2 relative">
                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-full"></div>
                      <div className="h-1.5 bg-black rounded w-1/4 absolute top-0 left-1/3 redaction-box"></div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1 bg-gray-300 rounded w-5/6"></div>
                    </div>

                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-1.5 bg-black rounded w-2/3 absolute top-0 left-0 redaction-box"></div>
                    </div>

                    <div className="space-y-1">
                      <div className="h-1 bg-gray-300 rounded w-full"></div>
                    </div>

                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-4/5"></div>
                      <div className="h-1.5 bg-black rounded w-1/2 absolute top-0 left-0 redaction-box"></div>
                    </div>

                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-5/6"></div>
                      <div className="h-1.5 bg-black rounded w-1/3 absolute top-0 left-0 redaction-box"></div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1 bg-gray-300 rounded w-4/5"></div>
                    </div>

                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-3/5"></div>
                      <div className="h-1.5 bg-black rounded w-3/5 absolute top-0 left-0 redaction-box"></div>
                    </div>

                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-full"></div>
                      <div className="h-1.5 bg-black rounded w-2/5 absolute top-0 left-1/2 redaction-box"></div>
                    </div>
                    <div className="space-y-1">
                      <div className="h-1 bg-gray-300 rounded w-2/3"></div>
                    </div>

                    <div className="space-y-1 relative redaction-line">
                      <div className="h-1 bg-gray-300 rounded w-4/6"></div>
                      <div className="h-1.5 bg-black rounded w-2/5 absolute top-0 left-0 redaction-box"></div>
                    </div>

                    <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-500 to-transparent shadow-[0_0_10px_rgba(16,185,129,0.8)] animate-scan-line"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <Link to="/register">
              <Button size="lg" className="text-base font-semibold px-8 bg-white text-black hover:bg-gray-200">
                Get Started for Free
              </Button>
            </Link>
            <a href="https://demo.suredox.co.za/demo/cmnz3nbkt2r9k1q1e06mtfhjf?utm_source=link" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                variant="outline"
                className="text-base font-semibold px-8 bg-transparent text-white border-white hover:bg-white/10"
              >
                View Demo
              </Button>
            </a>
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 space-y-4">
            <h2 className="text-5xl font-black tracking-tight text-balance text-white">
              Everything you need for POPIA de-identification.
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl">
              Built for South African enterprises, legal teams and compliance officers, who need to protect personal information.
            </p>
          </div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="space-y-4 border border-white/20 p-8 rounded-lg hover:border-white/40 transition-colors bg-white/5 backdrop-blur-sm"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-gray-900" />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 border-t border-white/20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 text-center space-y-4">
            <h2 className="text-5xl font-black tracking-tight text-balance text-white">
              Simple, transparent pricing
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Pay only for what you use. Buy credits in bulk and save more.
              All new accounts start with 10 free credits.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">POPIA</span>
                <span>1 credit for text or scanned PDF · 5 credits for image or handwritten PDF</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">Categories</span>
                <span>1 credit per 10 pages · Text PDFs only</span>
              </div>
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto">
            {CREDIT_PACKS.map((pack) => (
              <div
                key={pack.id}
                className={`
                  relative flex flex-col p-6 rounded-xl border transition-all
                  ${pack.popular
                    ? 'border-emerald-400 bg-white/10 shadow-lg shadow-emerald-500/20 scale-105'
                    : 'border-white/20 bg-white/5 hover:border-white/40'
                  }
                `}
              >
                {pack.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                      <Sparkles className="w-3 h-3" />
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-white mb-1 capitalize">
                    {pack.id}
                  </h3>
                  <div className="text-4xl font-black text-white mb-1">
                    R{pack.price}
                  </div>
                  <div className="text-sm text-gray-400">
                    {pack.credits} credits
                  </div>
                </div>

                <div className="space-y-3 mb-6 flex-1">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>R{pack.pricePerCredit.toFixed(2)} per credit</span>
                  </div>
                  {pack.savings > 0 && (
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <Sparkles className="w-4 h-4 flex-shrink-0" />
                      <span>Save {pack.savings}%</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Credits never expire</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{pack.description}</span>
                  </div>
                </div>

                <Link to="/register" className="w-full">
                  <Button
                    size="sm"
                    className={`w-full font-semibold ${
                      pack.popular
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                        : 'bg-white text-black hover:bg-gray-200'
                    }`}
                  >
                    Get Started
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 text-sm">
              Need custom pricing for your enterprise?{' '}
              <button onClick={() => setShowContact(true)} className="text-emerald-400 hover:text-emerald-300 font-medium">
                Contact us
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 border-t border-white/20">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="text-4xl font-black tracking-tight text-white mb-6">
            Ready to protect your data?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Start with 10 free credits. No credit card required.
          </p>
          <Link to="/register">
            <Button size="lg" className="text-base font-semibold px-8 bg-white text-black hover:bg-gray-200">
              Get Started for Free
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="gradient" />

      {/* Contact Dialog */}
      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent className="bg-gray-900 border border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Contact Us</DialogTitle>
            <DialogDescription className="text-gray-300">
              Get in touch with us via email:
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <a
              href="mailto:eben@suredox.co.za"
              className="text-emerald-400 hover:text-emerald-300 font-medium text-lg"
            >
              eben@suredox.co.za
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
